import { useQuery } from "@tanstack/react-query";
import { useUser } from "../../../contexts/UserContext";
import { fetchExpensesByCategoryForPeriod } from "../../../domain/services/transactions/expenseByCategoryForPeriod";
import {
  DATE_RANGE_TO_API_PERIOD,
  DATE_RANGES,
  DateRange,
} from "../utils/dateUtils";
import { fetchIncomeStatementSummary } from "../../../domain/services/transactions/incomeStatement";

import { format, parse, startOfWeek, endOfWeek } from "date-fns";
import { generateChartColors } from "../utils/analyticsUtils";
import { useFamilyTransactions } from "./useFamilyTransactions";

/**
 * Processes income statement data based on the specified timeframe.
 * - 12 months: Groups by month with month name labels
 * - 30 days: Groups by week with week range labels
 * - 7 days: Shows daily data with weekday name labels
 */
const processIncomeStatementData = (
  data: Record<string, { income: number; expenses: number }>,
  timeframe: DateRange,
) => {
  if (timeframe === DATE_RANGES.YEAR) {
    // Group by month
    return Object.entries(data).reduce(
      (acc, [date, values]) => {
        const monthKey = format(
          parse(date, "yyyy-MM-dd", new Date()),
          "MMM yyyy",
        );
        const existingData = acc.find((item) => item.name === monthKey);

        if (existingData) {
          existingData.expenses += values.expenses;
          existingData.income += Math.abs(values.income);
        } else {
          acc.push({
            name: monthKey,
            expenses: values.expenses,
            income: Math.abs(values.income),
          });
        }
        return acc;
      },
      [] as Array<{ name: string; expenses: number; income: number }>,
    );
  }

  if (timeframe === DATE_RANGES.MONTH) {
    // Group by week
    return Object.entries(data).reduce(
      (acc, [date, values]) => {
        const parsedDate = parse(date, "yyyy-MM-dd", new Date());
        const weekStart = format(startOfWeek(parsedDate), "MMM d");
        const weekEnd = format(endOfWeek(parsedDate), "MMM d");
        const weekKey = `${weekStart} - ${weekEnd}`;

        const existingData = acc.find((item) => item.name === weekKey);
        if (existingData) {
          existingData.expenses += values.expenses;
          existingData.income += Math.abs(values.income);
        } else {
          acc.push({
            name: weekKey,
            expenses: values.expenses,
            income: Math.abs(values.income),
          });
        }
        return acc;
      },
      [] as Array<{ name: string; expenses: number; income: number }>,
    );
  }

  // 7 days - daily view
  return Object.entries(data).map(([date, values]) => ({
    name: format(parse(date, "yyyy-MM-dd", new Date()), "EEE"),
    expenses: values.expenses,
    income: Math.abs(values.income),
  }));
};

export const useSpendingAnalytics = (timeframe: DateRange) => {
  const { currentUser } = useUser();
  const { data: transactionsResult } = useFamilyTransactions();

  return useQuery({
    queryKey: ["spendingAnalytics", currentUser?.uid, timeframe],
    queryFn: async () => {
      if (!currentUser) {
        return {
          categoryData: null,
          trendData: null,
          topMerchants: [],
        };
      }

      const accessToken = await currentUser.getIdToken();
      const period = DATE_RANGE_TO_API_PERIOD[timeframe];

      const [expensesByCategory, incomeStatement] = await Promise.all([
        fetchExpensesByCategoryForPeriod(period, accessToken),
        fetchIncomeStatementSummary(period, accessToken),
      ]);

      // Process category data (pie chart)
      const categoryData = {
        labels: expensesByCategory.map((item) => item.category),
        datasets: [
          {
            data: expensesByCategory.map((item) => item.amount),
            backgroundColor: generateChartColors(expensesByCategory.length),
          },
        ],
      };

      // Process trend data (line chart)
      const trendData = processIncomeStatementData(
        incomeStatement,
        timeframe,
      ).map((item) => ({
        name: item.name,
        expenses: item.expenses,
        income: Math.abs(item.income),
      }));

      const transactions = transactionsResult?.transactions || [];

      // Calculate top merchants from transactions
      const merchantTotals = transactions.reduce(
        (acc, transaction) => {
          const details = transaction.transaction_details;
          const merchantId = details.merchant_entity_id;

          if (!merchantId) return acc;

          if (!acc[merchantId]) {
            acc[merchantId] = {
              name: details.merchant_name || "Unknown Merchant",
              amount: 0,
              transactionCount: 0,
              logoUrl: details.logo_url,
            };
          }

          acc[merchantId].amount += Math.abs(transaction.amount);
          acc[merchantId].transactionCount += 1;

          return acc;
        },
        {} as Record<
          string,
          {
            name: string;
            amount: number;
            transactionCount: number;
            logoUrl?: string;
          }
        >,
      );

      // Convert to array and sort by amount
      const topMerchants = Object.values(merchantTotals || {})
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 3) // Get top 3 merchants
        .map((merchant) => ({
          ...merchant,
          trend: 0, // We can calculate trend by comparing with previous period later
        }));

      console.debug("topMerchants", topMerchants);

      return { categoryData, trendData, topMerchants };
    },
    enabled: !!currentUser?.uid && !!transactionsResult?.transactions,
  });
};
